import React, { useState, useMemo } from 'react';
import { Search, ChevronDown, X, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import Navigation from '../components/Navigation';
import { mockData } from '../mock';

const SpeakersPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const itemsPerPage = 12;

  const categories = [
    { id: 'all', label: 'All' },
    { id: 'speaker', label: 'Speakers' },
    { id: 'performer', label: 'Performers' },
    { id: 'host', label: 'Hosts' },
    { id: 'special', label: 'Special Guests' },
  ];

  const allTags = ['Leadership', 'Worship', 'Business', 'Youth', 'Community'];
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  const testimonials = [
    { quote: "Faith isn't silence—it's momentum.", author: "Dr. Sarah Mitchell", role: "Lead Pastor" },
    { quote: "Purpose discovered is power unleashed.", author: "Bishop David Chen", role: "Global Impact Leader" },
    { quote: "Every stage is sacred when you speak truth.", author: "Marcus Johnson", role: "Worship Leader" }
  ];

  // Filter speakers
  const filteredSpeakers = useMemo(() => {
    let result = mockData.allSpeakers;

    if (selectedCategory !== 'all') {
      result = result.filter(s => s.category === selectedCategory);
    }

    if (selectedTags.length > 0) {
      result = result.filter(s => 
        selectedTags.some(tag => s.tags.includes(tag))
      );
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(s => 
        s.name.toLowerCase().includes(query) ||
        s.role.toLowerCase().includes(query) ||
        s.organization.toLowerCase().includes(query)
      );
    }

    if (selectedLetter) {
      result = result.filter(s => 
        s.name.charAt(0).toUpperCase() === selectedLetter
      );
    }

    return result;
  }, [selectedCategory, selectedTags, searchQuery, selectedLetter]);

  const totalPages = Math.ceil(filteredSpeakers.length / itemsPerPage);
  const paginatedSpeakers = filteredSpeakers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const featuredSpeakers = mockData.allSpeakers.filter(s => 
    s.tags.includes('Keynote')
  ).slice(0, 3);

  const handleTagToggle = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setSelectedCategory('all');
    setSelectedTags([]);
    setSearchQuery('');
    setSelectedLetter(null);
    setCurrentPage(1);
  };

  const nextTestimonial = () => {
    setTestimonialIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setTestimonialIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Enhanced Page Header */}
      <div className="relative pt-32 pb-16 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-[#CFA64B]/5">
          <div className="absolute inset-0 opacity-5">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <pattern id="crowd-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                <circle cx="50" cy="50" r="2" fill="#0B1B3B" />
                <circle cx="25" cy="75" r="1.5" fill="#0B1B3B" />
                <circle cx="75" cy="25" r="1" fill="#0B1B3B" />
              </pattern>
              <rect width="100%" height="100%" fill="url(#crowd-pattern)" />
            </svg>
          </div>
        </div>

        <div className="relative max-w-[1440px] mx-auto px-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
            <a href="/" className="hover:text-[#CFA64B] transition-colors">Home</a>
            <span>/</span>
            <span className="text-[#0B1B3B] font-medium">Speakers & Guests</span>
          </div>
          
          {/* Title */}
          <h1 className="text-5xl md:text-6xl font-bold text-[#0B1B3B] mb-4 leading-tight">
            Speakers & Guests
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl">
            Meet the visionaries inspiring faith, culture, and purpose.
          </p>
        </div>
      </div>

      {/* Sticky Filter Bar */}
      <div className="sticky top-[80px] z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-[1440px] mx-auto px-6 py-4">
          {/* Category Chips & Search */}
          <div className="flex flex-wrap md:flex-nowrap items-center gap-3 mb-4">
            <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 scrollbar-hide flex-1">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => {
                    setSelectedCategory(cat.id);
                    setCurrentPage(1);
                  }}
                  className={`px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-200 ${
                    selectedCategory === cat.id
                      ? 'bg-[#CFA64B] text-[#0B1B3B] shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Tag Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowTagDropdown(!showTagDropdown)}
                className="px-5 py-2.5 rounded-full text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 flex items-center gap-2 whitespace-nowrap"
              >
                Tags {selectedTags.length > 0 && `(${selectedTags.length})`}
                <ChevronDown className="w-4 h-4" />
              </button>
              {showTagDropdown && (
                <div className="absolute top-full mt-2 right-0 bg-white rounded-2xl shadow-xl border border-gray-100 p-4 min-w-[220px] z-50">
                  {allTags.map(tag => (
                    <label
                      key={tag}
                      className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedTags.includes(tag)}
                        onChange={() => handleTagToggle(tag)}
                        className="rounded border-gray-300 text-[#CFA64B] focus:ring-[#CFA64B] w-4 h-4"
                      />
                      <span className="text-sm font-medium text-gray-700">{tag}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Search */}
            <div className="relative w-full md:w-72">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search speakers"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-11 pr-4 py-2.5 rounded-full border-gray-300 focus:border-[#CFA64B] focus:ring-[#CFA64B]"
              />
            </div>
          </div>

          {/* A-Z Filter */}
          <div className="flex gap-1.5 overflow-x-auto pb-2 scrollbar-hide">
            {alphabet.map(letter => (
              <button
                key={letter}
                onClick={() => {
                  setSelectedLetter(selectedLetter === letter ? null : letter);
                  setCurrentPage(1);
                }}
                className={`min-w-[32px] h-8 flex items-center justify-center rounded-lg text-sm font-semibold transition-all duration-200 ${
                  selectedLetter === letter
                    ? 'bg-[#CFA64B] text-[#0B1B3B] shadow-sm scale-110'
                    : 'hover:bg-gray-100 text-gray-600 hover:scale-105'
                }`}
              >
                {letter}
              </button>
            ))}
            {selectedLetter && (
              <button
                onClick={() => setSelectedLetter(null)}
                className="ml-3 px-4 h-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-semibold transition-colors"
              >
                Clear
              </button>
            )}
          </div>

          {/* Active Filters */}
          {(selectedCategory !== 'all' || selectedTags.length > 0 || searchQuery || selectedLetter) && (
            <div className="flex items-center gap-2 pt-4 mt-4 border-t border-gray-200">
              <span className="text-sm font-medium text-gray-600">Active filters:</span>
              {selectedCategory !== 'all' && (
                <span className="px-3 py-1.5 bg-[#CFA64B]/15 text-[#0B1B3B] rounded-full text-sm font-semibold border border-[#CFA64B]/20">
                  {categories.find(c => c.id === selectedCategory)?.label}
                </span>
              )}
              {selectedTags.map(tag => (
                <span key={tag} className="px-3 py-1.5 bg-[#CFA64B]/15 text-[#0B1B3B] rounded-full text-sm font-semibold flex items-center gap-2 border border-[#CFA64B]/20">
                  {tag}
                  <X className="w-3.5 h-3.5 cursor-pointer hover:opacity-70" onClick={() => handleTagToggle(tag)} />
                </span>
              ))}
              <button
                onClick={clearAllFilters}
                className="text-sm text-[#CFA64B] hover:text-[#BD954A] font-semibold ml-2 underline"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Keynote Spotlight */}
      {selectedCategory === 'all' && !searchQuery && !selectedLetter && selectedTags.length === 0 && currentPage === 1 && (
        <div className="relative bg-gradient-to-br from-[#0B1B3B] via-[#1a2f5a] to-[#0B1B3B] text-white py-20 overflow-hidden">
          {/* Decorative Elements */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#CFA64B] rounded-full filter blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl"></div>
          </div>

          <div className="relative max-w-[1440px] mx-auto px-6">
            <h2 className="text-4xl font-bold mb-3">Keynote Spotlight</h2>
            <p className="text-gray-300 text-lg mb-12">Hear from the leaders shaping the future of faith</p>
            <div className="grid md:grid-cols-3 gap-8">
              {featuredSpeakers.map(speaker => (
                <Card key={speaker.id} className="bg-white/5 backdrop-blur-md border-white/10 overflow-hidden group cursor-pointer hover:bg-white/10 transition-all duration-500 hover:-translate-y-2"
                  onClick={() => setSelectedSpeaker(speaker)}
                >
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <img
                      src={speaker.image}
                      alt={speaker.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                  </div>
                  <div className="p-8">
                    <h3 className="text-2xl font-bold mb-2 group-hover:text-[#CFA64B] transition-colors">{speaker.name}</h3>
                    <p className="text-gray-300 mb-4 font-medium">{speaker.role}</p>
                    <p className="text-sm text-gray-400 italic line-clamp-3 mb-6">\"{speaker.bio.substring(0, 120)}...\"</p>
                    <Button variant="outline" className="bg-white/10 hover:bg-white/20 text-white border-white/20 hover:border-white/40 w-full">
                      Read Full Bio
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Moments From the Stage */}
      {selectedCategory === 'all' && !searchQuery && !selectedLetter && selectedTags.length === 0 && currentPage === 1 && (
        <div className="py-20 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-[1440px] mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#0B1B3B] mb-3">Moments From the Stage</h2>
              <p className="text-lg text-gray-600">Capturing the impact and inspiration</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { title: 'Leading with Purpose', image: 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=600&h=400&fit=crop' },
                { title: 'Stories of Impact', image: 'https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=600&h=400&fit=crop' },
                { title: 'Worship & Wonder', image: 'https://images.unsplash.com/photo-1524368535928-5b5e00ddc76b?w=600&h=400&fit=crop' }
              ].map((moment, idx) => (
                <div key={idx} className="group relative overflow-hidden rounded-2xl aspect-[4/3] cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-500">
                  <img
                    src={moment.image}
                    alt={moment.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent group-hover:from-[#0B1B3B]/90 transition-all duration-500"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-[#CFA64B] transition-colors">{moment.title}</h3>
                    <div className="flex items-center gap-2 text-white/80 group-hover:text-white transition-colors">
                      <Play className="w-4 h-4" />
                      <span className="text-sm font-medium">Watch Highlights</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Voices of Inspiration - Testimonial Carousel */}
      {selectedCategory === 'all' && !searchQuery && !selectedLetter && selectedTags.length === 0 && currentPage === 1 && (
        <div className="py-20 bg-gradient-to-br from-[#CFA64B]/10 via-[#CFA64B]/5 to-white">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-[#0B1B3B] text-center mb-16">Voices of Inspiration</h2>
            <div className="relative">
              <div className="text-center">
                <div className="text-6xl text-[#CFA64B] mb-6">"</div>
                <p className="text-3xl md:text-4xl font-bold text-[#0B1B3B] mb-8 leading-relaxed">
                  {testimonials[testimonialIndex].quote}
                </p>
                <p className="text-xl font-semibold text-gray-700 mb-1">
                  — {testimonials[testimonialIndex].author}
                </p>
                <p className="text-gray-600">
                  {testimonials[testimonialIndex].role}
                </p>
              </div>

              {/* Navigation */}
              <div className="flex justify-center gap-4 mt-12">
                <button
                  onClick={prevTestimonial}
                  className="w-12 h-12 rounded-full bg-white border-2 border-gray-200 hover:border-[#CFA64B] flex items-center justify-center transition-all hover:scale-110 shadow-md"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-700" />
                </button>
                <button
                  onClick={nextTestimonial}
                  className="w-12 h-12 rounded-full bg-white border-2 border-gray-200 hover:border-[#CFA64B] flex items-center justify-center transition-all hover:scale-110 shadow-md"
                >
                  <ChevronRight className="w-5 h-5 text-gray-700" />
                </button>
              </div>

              {/* Dots */}
              <div className="flex justify-center gap-2 mt-6">
                {testimonials.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setTestimonialIndex(idx)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      idx === testimonialIndex ? 'w-8 bg-[#CFA64B]' : 'w-2 bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Speaker Grid */}
      <div className="max-w-[1440px] mx-auto px-6 py-16">
        {filteredSpeakers.length === 0 ? (
          <div className="text-center py-24">
            <div className="text-7xl mb-6">🔍</div>
            <h3 className="text-3xl font-bold text-[#0B1B3B] mb-3">No speakers found</h3>
            <p className="text-lg text-gray-600 mb-8">Try adjusting your filters or search query</p>
            <Button
              onClick={clearAllFilters}
              className="bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-semibold px-8 py-6 text-lg"
            >
              Clear all filters
            </Button>
          </div>
        ) : (
          <>
            {/* Results Count */}
            <div className="mb-8 flex justify-between items-center">
              <div className="text-sm font-medium text-gray-600">
                Showing {((currentPage - 1) * itemsPerPage) + 1}–{Math.min(currentPage * itemsPerPage, filteredSpeakers.length)} of {filteredSpeakers.length}
              </div>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-16">
              {paginatedSpeakers.map(speaker => (
                <Card
                  key={speaker.id}
                  className="overflow-hidden group cursor-pointer hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-0"
                  onClick={() => setSelectedSpeaker(speaker)}
                >
                  <div className="aspect-[3/4] overflow-hidden bg-gray-100 relative">
                    <img
                      src={speaker.image}
                      alt={speaker.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-[#0B1B3B] mb-2 group-hover:text-[#CFA64B] transition-colors leading-tight">
                      {speaker.name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2 leading-relaxed">{speaker.role}</p>
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {speaker.tags.slice(0, 2).map(tag => (
                        <span key={tag} className="px-2.5 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="text-sm text-[#CFA64B] font-semibold opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center gap-1">
                      View Profile →
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="border-gray-300 disabled:opacity-50"
                >
                  « Previous
                </Button>
                {[...Array(totalPages)].map((_, i) => (
                  <Button
                    key={i + 1}
                    variant={currentPage === i + 1 ? 'default' : 'outline'}
                    onClick={() => setCurrentPage(i + 1)}
                    className={currentPage === i + 1 ? 'bg-[#CFA64B] text-[#0B1B3B] hover:bg-[#BD954A] font-semibold' : 'border-gray-300'}
                  >
                    {i + 1}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="border-gray-300 disabled:opacity-50"
                >
                  Next »
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Become a Speaker CTA Strip */}
      {selectedCategory === 'all' && !searchQuery && !selectedLetter && selectedTags.length === 0 && (
        <div className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3B] via-[#1a2f5a] to-[#CFA64B]"></div>
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full filter blur-3xl"></div>
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#CFA64B] rounded-full filter blur-3xl"></div>
          </div>
          <div className="relative max-w-4xl mx-auto px-6 text-center text-white">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Have a message the world needs to hear?</h2>
            <p className="text-xl text-gray-200 mb-8">Join us on the stage and inspire thousands</p>
            <Button className="bg-white text-[#0B1B3B] hover:bg-gray-100 font-semibold px-10 py-6 text-lg shadow-xl hover:shadow-2xl transition-all hover:scale-105">
              Apply to Speak
            </Button>
          </div>
        </div>
      )}

      {/* Profile Modal */}
      {selectedSpeaker && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md" onClick={() => setSelectedSpeaker(null)}>
          <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Banner Image */}
            <div className="h-72 md:h-96 overflow-hidden relative">
              <img
                src={selectedSpeaker.image}
                alt={selectedSpeaker.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
              <button
                onClick={() => setSelectedSpeaker(null)}
                className="absolute top-6 right-6 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-all hover:scale-110 shadow-lg"
              >
                <X className="w-6 h-6 text-[#0B1B3B]" />
              </button>
            </div>

            {/* Content */}
            <div className="p-8 md:p-12">
              <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-3 leading-tight">
                {selectedSpeaker.name}
              </h2>
              <p className="text-xl text-gray-600 mb-2 font-medium">{selectedSpeaker.role}</p>
              <p className="text-gray-500 mb-8">{selectedSpeaker.organization}</p>

              {/* Tags */}
              <div className="flex flex-wrap gap-2.5 mb-10">
                {selectedSpeaker.tags.map(tag => (
                  <span key={tag} className="px-4 py-2 bg-[#CFA64B]/15 text-[#0B1B3B] rounded-full text-sm font-semibold border border-[#CFA64B]/20">
                    {tag}
                  </span>
                ))}
              </div>

              {/* Bio */}
              <div className="mb-10">
                <h3 className="text-2xl font-bold text-[#0B1B3B] mb-4">About</h3>
                <p className="text-gray-700 leading-relaxed text-lg">{selectedSpeaker.bio}</p>
              </div>

              {/* Sessions */}
              {selectedSpeaker.sessions && selectedSpeaker.sessions.length > 0 && (
                <div className="mb-10">
                  <h3 className="text-2xl font-bold text-[#0B1B3B] mb-5">Session Highlights</h3>
                  <div className="space-y-4">
                    {selectedSpeaker.sessions.map((session, idx) => (
                      <div key={idx} className="p-6 bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-100 hover:shadow-md transition-shadow">
                        <h4 className="font-bold text-[#0B1B3B] mb-3 text-lg">{session.title}</h4>
                        <div className="flex flex-wrap gap-6 text-sm text-gray-600">
                          <span className="flex items-center gap-2">
                            <span className="text-lg">📅</span> {session.day}
                          </span>
                          <span className="flex items-center gap-2">
                            <span className="text-lg">🕐</span> {session.time}
                          </span>
                          <span className="flex items-center gap-2">
                            <span className="text-lg">📍</span> {session.stage}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button className="flex-1 bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-bold py-6 text-lg shadow-lg hover:shadow-xl transition-all">
                  Add to Schedule
                </Button>
                <Button variant="outline" className="flex-1 border-2 border-[#CFA64B] text-[#CFA64B] hover:bg-[#CFA64B]/10 font-bold py-6 text-lg">
                  Follow Updates
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-[#0f172a] text-white py-12">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4 text-[#CFA64B]">KNOWN CON</h3>
              <p className="text-gray-400">Where belief meets impact.</p>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-lg">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/" className="hover:text-[#CFA64B] transition-colors">Home</a></li>
                <li><a href="/speakers" className="hover:text-[#CFA64B] transition-colors">Speakers</a></li>
                <li><a href="#experience" className="hover:text-[#CFA64B] transition-colors">Experience</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-lg">Get Involved</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#sponsor" className="hover:text-[#CFA64B] transition-colors">Become a Sponsor</a></li>
                <li><a href="#volunteer" className="hover:text-[#CFA64B] transition-colors">Volunteer</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-lg">Contact</h4>
              <p className="text-gray-400 text-sm">info@knowncon.com</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            © 2025 KNOWN CON. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SpeakersPage;
